import React, { useCallback, useMemo, useState } from 'react';
import { isMobileScreen } from '@klp/core/app/config';
import { ParamType } from '@forvalter/utils/ParamType';
import { getFromStorage, saveToStorage } from '@forvalter/utils/storage';
import { LayoutContent } from './LayoutContent';
import { LayoutContext } from './LayoutContext';
import type { CssVariable } from './LayoutContext';

export const Layout = ({ children }: React.PropsWithChildren<unknown>) => {
    const [decorate, setDecorate] = useState(false);
    const [withoutSidebar, setWithoutSidebar] = useState(false);
    const [withoutHeader, setWithoutHeader] = useState(false);
    const [fullScreen, setFullScreen] = useState(false);
    const [centered, setCentered] = useState(false);
    const [withBackground, setWithBackground] = useState(false);

    // Menu state management (moved from Redux)
    const [menuPin, setMenuPinState] = useState<boolean>(
        () => !!getFromStorage(ParamType.menuPin),
    );
    const [isMobile, setIsMobileState] = useState<boolean>(() =>
        isMobileScreen(),
    );
    const [menuOpen, setMenuOpenState] = useState<boolean>(() =>
        isMobileScreen() ? false : !!getFromStorage(ParamType.menuPin),
    );
    const [tabOpen, setTabOpenState] = useState<boolean>(
        () => !!getFromStorage('tabOpen'),
    );

    const setLayoutCssProperty = useCallback(
        (vars: CssVariable | Array<CssVariable>) => {
            const entries = Array.isArray(vars) ? vars : [vars];
            entries.forEach(({ name, value }) => {
                document.body.style.setProperty(name, value);
            });
        },
        [],
    );

    const setMenuPin = useCallback((pin: boolean) => {
        setMenuPinState(pin);
        saveToStorage(ParamType.menuPin, !!pin);
    }, []);

    const setIsMobile = useCallback(
        (mobile: boolean) => {
            setIsMobileState((prevIsMobile) => {
                if (prevIsMobile === mobile) {
                    return prevIsMobile;
                }

                // Auto-adjust menu when switching between mobile/desktop
                setMenuOpenState((_prevMenuOpen) => (mobile ? false : menuPin));

                return mobile;
            });
        },
        [menuPin],
    );

    const setMenuOpen = useCallback((open: boolean) => {
        setMenuOpenState(open);
    }, []);

    const toggleMenu = useCallback(
        (open?: boolean) => {
            setMenuOpenState((prevMenuOpen) => {
                if (isMobile) {
                    // Mobile: always toggle or set to explicit value
                    return open !== undefined ? open : !prevMenuOpen;
                }

                // Desktop with pin: always stay open
                if (menuPin) {
                    return true;
                }

                // Desktop without pin: toggle or set to explicit value
                return open !== undefined ? open : !prevMenuOpen;
            });
        },
        [isMobile, menuPin],
    );

    const setTabOpen = useCallback((open: boolean) => {
        setTabOpenState(open);
        saveToStorage('tabOpen', open);
    }, []);

    const toggleTab = useCallback((open?: boolean) => {
        setTabOpenState((prevTabOpen) => {
            const newState = open !== undefined ? open : !prevTabOpen;
            saveToStorage('tabOpen', newState);
            return newState;
        });
    }, []);

    const contextValue = useMemo(
        () => ({
            decorate,
            withoutSidebar,
            withoutHeader,
            fullScreen,
            centered,
            withBackground,
            menuOpen,
            menuPin,
            isMobile,
            tabOpen,
            setDecorate,
            setWithoutSidebar,
            setWithoutHeader,
            setFullScreen,
            setCentered,
            setWithBackground,
            setLayoutCssProperty,
            setMenuOpen,
            toggleMenu,
            setMenuPin,
            setIsMobile,
            setTabOpen,
            toggleTab,
        }),
        [
            decorate,
            withoutSidebar,
            withoutHeader,
            fullScreen,
            centered,
            withBackground,
            menuOpen,
            menuPin,
            isMobile,
            tabOpen,
            setLayoutCssProperty,
            setMenuOpen,
            toggleMenu,
            setMenuPin,
            setIsMobile,
            setTabOpen,
            toggleTab,
        ],
    );

    return (
        <LayoutContext.Provider value={contextValue}>
            <LayoutContent>{children}</LayoutContent>
        </LayoutContext.Provider>
    );
};
